import { html, SP } from '../../../html.js';
import type { VNode } from '../../../html.js';
import type { TenantDetail } from '../../api.js';
import type { DashboardActions, DashboardState } from '../../store.js';
import { Verdict, When, fmtInt } from '../../ui/bits.js';
import { BrandHead } from '../../ui/brand.js';
import { OwnershipPanel } from '../../ui/ownership.js';
import { isRegistered } from '../../ui/capcard.js';

/** The domain a claim is about, as a registrar spells it. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * Tab one: the answer at a glance.
 *
 * This is the only tab with nothing collapsed on it. A person opening a site
 * has one question — is it up and is anything wrong with it — and every other
 * tab is what they open once the answer is "something is". So the four numbers
 * come first, then the probe that refreshes the one number nobody can take on
 * trust, then who the domain belongs to.
 *
 * The ownership panel is the WHOLE process now, not a report on it. It used to
 * be four read-only rows and, when the claim was absent, a sentence sending the
 * reader off to run another crawl — because a crawl's result screen was the only
 * place in the product that could issue a challenge. So renewing a lapsed claim
 * cost a full crawl of the customer's own origin. The panel below is the same
 * component that result screen mounts (`ui/ownership.ts`): the state in words,
 * the three proofs with the token, and one Check now button.
 *
 * The `facts` list underneath is deliberately the boring half — dates and the
 * verifier's last sentence — because the panel above already says what is true
 * and what to do, and a person who wants the audit trail wants exact stamps.
 */
export function OverviewTab(props: {
  t: TenantDetail;
  state: DashboardState;
  actions: DashboardActions;
  now: number;
}): VNode {
  const { t, state, actions, now } = props;
  const faults = t.capabilities.filter((c) => (t.issues[c.name] ?? []).length > 0).length;
  const off = t.capabilities.filter((c) => !isRegistered(c)).length;
  const served = t.capabilities.length - off;

  return html`
    <div class="tabpanel">
      <div class="stats">
        <div class="stat">
          <p class="stat__label">Tools answering</p>
          <p class="stat__value">${fmtInt(served)}</p>
          <p class="stat__note">
            ${off
              ? html`${off} more ${off === 1 ? 'is' : 'are'} in the manifest with both surfaces
                  off — the crawl ranked ${off === 1 ? 'it' : 'them'} below its usefulness
                  threshold.${SP}
                  <button type="button" class="linkish" onClick=${() => actions.goTenantTab('tools')}>
                    See why, and switch ${off === 1 ? 'it' : 'them'} on
                  </button>`
              : html`${t.views.length} ${t.views.length === 1 ? 'view' : 'views'} render their
                  answers`}
          </p>
        </div>
        <div class="stat">
          <p class="stat__label">Calls in window</p>
          <p class="stat__value">${fmtInt(t.usage.calls)}</p>
          <p class="stat__note">
            ${t.usage.from && t.usage.to ? html`${t.usage.from} → ${t.usage.to}` : 'window not reported'}${SP}·${SP}
            <button type="button" class="linkish" onClick=${() => actions.setUsageTenant(t.tenantId)}>
              Open traffic for this site
            </button>
          </p>
        </div>
        <div class="stat">
          <p class="stat__label">Health</p>
          <p class="stat__value stat__value--verdict">${Verdict({ verdict: t.health.verdict })}</p>
          <p class="stat__note">
            last checked${SP}
            ${When({ iso: t.health.lastCheckedAt, now, absent: 'never — no probe has run' })}
          </p>
        </div>
        <div class="stat">
          <p class="stat__label">Faults</p>
          <p class=${faults ? 'stat__value stat__value--warn' : 'stat__value'}>${fmtInt(faults)}</p>
          <p class="stat__note">
            ${faults
              ? html`answered with rows and <code>ok:true</code> whatever they are passed`
              : 'no tool advertises a parameter its binding cannot honour'}
          </p>
        </div>
      </div>

      <section class="probe" aria-labelledby="probe-h">
        <h2 class="sec__h" id="probe-h">Check it now</h2>
        <button type="button" class="btn" disabled=${state.checking} onClick=${() => actions.checkHealth()}>
          ${state.checking ? 'Probing the live origin…' : 'Check health now'}
        </button>
        <p class="sec__p">
          A probe calls every tool against the origin at the manifest's own rate limit. It takes as
          long as it takes; nothing on this page advances until a result arrives.
        </p>
      </section>

      <section class="claim" aria-labelledby="claim-h">
        <h2 class="sec__h" id="claim-h">Domain ownership</h2>
        ${OwnershipPanel({
          domain: hostOf(t.source.canonicalOrigin),
          claim: t.ownership,
          openChallenge: t.openChallenge,
          challenge: state.proofs,
          verification: state.proofResult,
          issuing: state.issuingProofs,
          checking: state.checkingClaim,
          error: state.ownershipError,
          copied: state.copied,
          onCopy: actions.copy,
          onIssue: () => actions.issueProofs(),
          onCheck: () => actions.checkClaim(),
        })}
        ${t.ownership
          ? html`<dl class="facts">
              <div class="facts__row">
                <dt class="facts__k">Domain</dt>
                <dd class="facts__v">${t.ownership.domain ?? 'not recorded'}</dd>
              </div>
              <div class="facts__row">
                <dt class="facts__k">Method</dt>
                <dd class="facts__v">${t.ownership.method ?? 'not recorded'}</dd>
              </div>
              <div class="facts__row">
                <dt class="facts__k">Verified</dt>
                <dd class="facts__v">
                  ${When({ iso: t.ownership.verifiedAt ?? null, now, absent: 'not yet' })}
                </dd>
              </div>
              <div class="facts__row">
                <dt class="facts__k">Expires</dt>
                <dd class="facts__v">
                  ${When({ iso: t.ownership.expiresAt ?? null, now, absent: 'no deadline recorded' })}
                </dd>
              </div>
              <div class="facts__row">
                <dt class="facts__k">Last checked</dt>
                <dd class="facts__v">
                  ${t.ownership.lastCheckOk ? 'the proof was there' : 'the proof was not found'}${SP}·${SP}
                  ${t.ownership.lastCheckDetail || 'no detail recorded'}
                </dd>
              </div>
            </dl>`
          : null}
      </section>

      ${BrandHead({
        identity: t.identity,
        brand: t.brand,
        origin: t.source.canonicalOrigin,
        tools: t.capabilities.length,
      })}
    </div>
  `;
}
